import {
    Modal,
    Stack,
    Select,
    TextInput,
    Group,
    Button,
    Text,
    useMantineColorScheme,
    Box,
    LoadingOverlay
} from "@mantine/core";
import { Database, Save } from "lucide-react";
import { DataSource } from "@bi-studio/shared/lib";

interface PlaygroundSaveModalProps {
    opened: boolean;
    onClose: () => void;
    dataSources: DataSource[];
    selectedDataSource: string | null;
    onSelectedDataSourceChange: (val: string | null) => void;
    tableName: string;
    onTableNameChange: (val: string) => void;
    onSave: () => void;
    isSaving: boolean;
}

export function PlaygroundSaveModal({
    opened,
    onClose,
    dataSources,
    selectedDataSource,
    onSelectedDataSourceChange,
    tableName,
    onTableNameChange,
    onSave,
    isSaving
}: PlaygroundSaveModalProps) {
    const { colorScheme } = useMantineColorScheme();

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Save Data to Database"
            centered
            size="md"
            overlayProps={{ backgroundOpacity: 0.1, blur: 2 }}
        >
            <LoadingOverlay visible={isSaving} overlayProps={{ backgroundOpacity: 0.1, blur: 2 }} />
            <Stack gap="md">
                <Text size="sm" c={colorScheme === 'light' ? 'gray.7' : 'dimmed'}>
                    Choose a target database connection and provide a name for the new table.
                </Text>

                <Box
                    p="md"
                    style={{
                        border: '1px solid var(--modal-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                        background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
                    }}
                >
                    <Stack gap="md">
                        <Select
                            label="Target Database"
                            placeholder="Pick a database"
                            data={dataSources.map(ds => ({ value: ds.id, label: ds.name }))}
                            value={selectedDataSource}
                            onChange={onSelectedDataSourceChange}
                            leftSection={<Database size={16} />}
                            required
                        />

                        <TextInput
                            label="Table Name"
                            placeholder="e.g. sales_2024"
                            description="Will be prefixed with 'imported_'"
                            descriptionProps={{ c: colorScheme === 'light' ? 'gray.7' : undefined }}
                            value={tableName}
                            onChange={(e) => onTableNameChange(e.currentTarget.value)}
                            required
                        />
                    </Stack>
                </Box>

                <Group justify="flex-end" pt="md">
                    <Button
                        variant={colorScheme === 'dark' ? 'light' : 'subtle'}
                        color="gray"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="filled"
                        color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
                        leftSection={<Save size={16} />}
                        loading={isSaving}
                        disabled={!selectedDataSource || !tableName}
                        onClick={onSave}
                    >
                        Save to Table
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
